import React, { useState } from "react";
import { formatBotMessage } from "./utils/formatMessage";
import { BATCH_ANSWER_KIND } from "./utils/batchAnswer";
import "./QuestionCard.css";

const NO_PICKS = Object.freeze({});

/** Stable identity of a batch card: its question ids in order ("" when there is no batch). */
export const batchKeyOf = (questions) => (questions ? questions.map((q) => q.question_id).join("|") : "");

/**
 * Picks for one batch card, reset whenever a different batch arrives (the key changes). Owned by whoever sends the
 * batch: CopilotPanel (the composer's send button is THE send, owner 23 Oct) or QuestionCard when used standalone.
 * answer() is the onAnswer value {kind: "batch", picks} once every question is picked, else null.
 */
export const useBatchPicks = (questions) => {
  const key = batchKeyOf(questions);
  const [state, setState] = useState({ key, picks: NO_PICKS });
  const picks = state.key === key ? state.picks : NO_PICKS;
  const total = questions ? questions.length : 0;
  const answered = questions ? questions.filter((q) => picks[q.question_id]).length : 0;
  const complete = total > 0 && answered === total;
  const pick = (questionId, optionId) =>
    setState((prev) => ({ key, picks: { ...(prev.key === key ? prev.picks : NO_PICKS), [questionId]: optionId } }));
  const answer = () =>
    (complete ? { kind: BATCH_ANSWER_KIND, picks: Object.fromEntries(questions.map((q) => [q.question_id, picks[q.question_id]])) } : null);
  return { picks, pick, answered, total, complete, answer };
};

/**
 * QuestionBatchBody — the body of a Diagnostic Agent card that asks several questions at once
 * (tse:question `questions`, at most MAX_BATCH_QUESTIONS). Each question is a block with its text and
 * its options as pills; a click picks (or re-picks) that question's answer via onPick. No Send of its own:
 * the composer's send button sends the batch (owner 23 Oct: "the main send button is enough").
 */
export const QuestionBatchBody = ({ questions, picks = NO_PICKS, onPick, disabled = false }) => (
  <div className="d5-qbatch">
    {questions.map((q, qi) => (
      <div key={q.question_id} className="d5-qbatch-item" role="group" aria-label={q.text}>
        <div className="d5-qbatch-text">
          <span className="d5-qbatch-num">{qi + 1}.</span>
          <span dangerouslySetInnerHTML={{ __html: formatBotMessage(q.text) }} />
        </div>
        <div className="d5-question-options-pills">
          {(q.options || []).map((o) => {
            const on = picks[q.question_id] === o.option_id;
            return (
              <button
                key={o.option_id}
                type="button"
                aria-pressed={on}
                disabled={disabled}
                className={`d5-question-option d5-question-pill${on ? " d5-question-pill-picked" : ""}`}
                onClick={() => onPick?.(q.question_id, o.option_id)}
              >
                <span dangerouslySetInnerHTML={{ __html: formatBotMessage(o.label) }} />
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

/**
 * The answered batch in the transcript, LEFT side (owner 23 Oct, screenshot 55: the V4 answered card): one read-only card
 * under the copilot's reply, each question with every option it offered as wrapping pills, several per line (owner 23 Oct:
 * up to 5 questions must stay compact), the chosen one highlighted and the rest muted. Same .d5-answered-* family as an answered single card; nothing here is clickable.
 */
export const AnsweredBatchCard = ({ lines = [] }) => (
  <div className="d5-answered-card d5-answered-batch">
    {lines.map((l, i) => (
      <div key={i} className="d5-answered-batch-item">
        <div className="d5-answered-question" dangerouslySetInnerHTML={{ __html: formatBotMessage(l.question) }} />
        <div className="d5-answered-options d5-answered-options-pills">
          {(l.options?.length ? l.options : [l.answer]).map((opt, oi) => (
            <div
              key={oi}
              className={`d5-answered-option${opt === l.answer ? " d5-answered-option-picked" : ""}`}
              dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/** Your reply on the RIGHT: a plain user bubble body, one line per question (muted question label, then the answer). */
export const AnsweredBatchSummary = ({ lines = [] }) => (
  <div className="d5-qbatch-summary">
    {lines.map((l, i) => (
      <div key={i} className="d5-qbatch-summary-line">
        <div className="d5-qbatch-summary-q">{l.question}</div>
        <div className="d5-qbatch-summary-a">{l.answer}</div>
      </div>
    ))}
  </div>
);
